import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../contexts/ContextProvider';

import Button from '../components/Button';
import IconButton from '../components/IconButton';
import Screen from '../components/Screen';
import Text from '../components/Text';
import { useAbonnementPush } from '../api/useAbonnementPush';
import {
    enregistrerApercu,
    envoyerNotificationDeTest,
    usePreferencesNotifications,
} from '../api/usePreferencesNotifications';
import { regimeDegradeIOS, versionIOS } from '../helpers/plateforme';
import { t } from '../i18n/langue';
import AmorceNotifications from './AmorceNotifications';
import BlocDeconnexionAppareils from './etatNotifications/BlocDeconnexionAppareils';
import ListeDiagnostic from './etatNotifications/ListeDiagnostic';

/**
 * L'écran « État des notifications » — #21, #28, #54, #80.
 *
 * ## Ce qu'il empêche
 *
 * Un téléphone **ne doit jamais pouvoir être sourd en silence**. La chaîne du push
 * casse à plusieurs endroits — permission retirée, endpoint purgé sur un 410,
 * sanction de push silencieux de WebKit, bascule d'établissement, réglage de compte
 * — et **aucun de ces points ne se voit depuis les autres**. C'est la réponse à
 * « je crois avoir tout fait et je n'ai rien reçu ».
 *
 * Chaque point est donc **réparable**, ou dit explicitement pourquoi il ne l'est
 * pas depuis l'app. Un état cassé se répare depuis un écran, jamais depuis un
 * silence.
 *
 * ## Hors registre, comme `/diagnostic`
 *
 * Une entrée au registre compterait dans les seuils 0/1/≥2 de #17, et un compte
 * n'ayant que la messagerie basculerait de « un seul écran » à « deux » alors que
 * rien n'a changé pour lui. C'est une sous-vue du compte, pas un écran.
 */

/** La cause de la surdité : la phrase du dictionnaire, plutôt que le code technique. */
const CAUSES: Record<string, string> = {
    assignment_filter_no_assignments: 'notifications.etat.causes.filtreAssignation',
    all_channels_off: 'notifications.etat.causes.tousCanauxCoupes',
    some_channels_off: 'notifications.etat.causes.certainsCanauxCoupes',
};

function texteCause(code: string | null | undefined): string {
    const cle = CAUSES[code ?? ''];

    return cle ? t(cle) : '';
}

/** Au-delà, un `ready` qui n'a pas répondu vaut « non enregistré ». */
const DELAI_READY_MS = 5000;

export default function EtatNotifications() {
    const navigate = useNavigate();
    const { currentFacility } = useStateContext();
    const { supporte, permission, reabonner, relirePermission } = useAbonnementPush();
    const { data, isLoading, refetch } = usePreferencesNotifications();

    const [enregistre, setEnregistre] = useState<boolean | null>(null);
    const [verification, setVerification] = useState(0);
    const [amorceOuverte, setAmorceOuverte] = useState(false);
    const [apercu, setApercu] = useState<boolean | null>(null);
    const [testEnvoye, setTestEnvoye] = useState<number | null>(null);

    /*
     * « Enregistré » = un abonnement push existe. `ready` et JAMAIS `getRegistration()`
     * (régression #72 : `undefined` tant que l'enregistrement n'est pas fini). Puis
     * l'abonnement lui-même : un service worker seul ne prouve rien, c'est le
     * quatrième état de #20 que l'ancien contrôle affichait au vert.
     */
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
            setEnregistre(false);
            return;
        }

        let vivant = true;
        /*
         * `ready` ne se résout JAMAIS si aucun service worker n'a été enregistré :
         * « Vérification en cours… » restait affiché pour toujours, et
         * « Réenregistrer » devenait inatteignable (audit 2026-09-23). Passé
         * `DELAI_READY_MS`, on conclut « non enregistré » — sans renoncer : un
         * `ready` tardif corrige encore l'état. Toujours pas de `getRegistration()`.
         */
        const delai = window.setTimeout(() => {
            if (vivant) setEnregistre((actuel) => actuel ?? false);
        }, DELAI_READY_MS);

        void navigator.serviceWorker.ready
            .then((r) => r.pushManager.getSubscription())
            .then((abonnement) => {
                window.clearTimeout(delai);
                if (vivant) setEnregistre(Boolean(abonnement));
            })
            .catch(() => {
                window.clearTimeout(delai);
                if (vivant) setEnregistre(false);
            });

        return () => {
            vivant = false;
            window.clearTimeout(delai);
        };
    }, [verification]);

    const prefs = data?.data;
    const apercuAffiche = apercu ?? prefs?.notify_show_preview ?? true;

    const basculerApercu = async () => {
        const cible = !apercuAffiche;
        setApercu(cible);
        try {
            await enregistrerApercu(cible);
        } catch {
            setApercu(!cible); // On rend la main à l'état réel plutôt que de mentir.
        }
    };

    // « Plus abonné » suppose qu'il l'a été : c'est faux tant que la permission
    // n'est pas accordée (audit 2026-09-23).
    // Refusée, seule l'app Réglages peut rendre la permission : on y renvoie, comme
    // le point « Notifications autorisées » (relecture 1b).
    const detailNonEnregistre =
        permission === 'granted'
            ? t('notifications.etat.plusAbonne')
            : permission === 'denied'
              ? t('notifications.etat.pasAbonneRefuse')
              : t('notifications.etat.pasAbonne');

    const versionAffichee = versionIOS();
    const degrade = regimeDegradeIOS();

    // L'amorce en ligne : quelqu'un qui n'a jamais répondu (permission « default »)
    // peut activer depuis cet écran, sans attendre un prochain démarrage (#80).
    if (amorceOuverte) {
        return (
            <AmorceNotifications
                onTermine={() => {
                    setAmorceOuverte(false);
                    relirePermission();
                    setVerification((v) => v + 1);
                    void refetch();
                }}
            />
        );
    }

    return (
        <Screen>
            <header className="flex shrink-0 items-center gap-2 border-b border-border-color-light bg-white px-3 py-2">
                <IconButton libelle={t('commun.retour')} onPress={() => navigate(-1)}>
                    <ArrowLeftIcon className="h-6 w-6 text-text-color" />
                </IconButton>
                <Text variante="sousTitre" as="h1">
                    {t('notifications.etat.titre')}
                </Text>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto pb-8">
                <ListeDiagnostic
                    supporte={supporte}
                    permission={permission}
                    enregistre={enregistre}
                    detailNonEnregistre={detailNonEnregistre}
                    nomFacilite={currentFacility?.name ?? '—'}
                    degrade={degrade}
                    versionAffichee={versionAffichee}
                />

                {/* La portée réelle des réglages — affichée SEULEMENT si elle
                    retranche. La chaîne est une chaîne de pannes, pas un inventaire :
                    sur le parc actuel, cette section n'apparaîtrait jamais. */}
                {!isLoading && prefs && prefs.effective_reach !== 'all' && (
                    <div className="mx-4 mt-4 overflow-hidden rounded-2xl bg-white px-5 py-4">
                        <Text
                            variante="petit"
                            ton={prefs.effective_reach === 'none' ? 'danger' : 'normal'}
                            className="font-medium"
                        >
                            {prefs.effective_reach === 'none'
                                ? t('notifications.etat.aucune')
                                : t('notifications.etat.partie')}
                        </Text>
                        <Text variante="petit" ton="attenue" className="mt-1 block leading-snug">
                            {t('notifications.etat.modifiableSurOrdinateur', {
                                cause: texteCause(prefs.effective_reach_cause),
                            })}
                        </Text>
                    </div>
                )}

                {/* L'interrupteur d'aperçu : TOUJOURS présent, même quand tout va
                    bien. C'est une commande, pas un diagnostic — il échappe donc à la
                    règle conditionnelle ci-dessus (#54 §7). */}
                <div className="mx-4 mt-4 overflow-hidden rounded-2xl bg-white px-5 py-4">
                    <Button
                        libelle={
                            apercuAffiche
                                ? t('notifications.etat.masquerApercu')
                                : t('notifications.etat.afficherApercu')
                        }
                        onPress={() => void basculerApercu()}
                        variante="secondaire"
                        pleineLargeur
                    />
                    <Text variante="petit" ton="attenue" className="mt-2 block leading-snug">
                        {apercuAffiche
                            ? t('notifications.etat.apercuAffiche')
                            : t('notifications.etat.apercuMasque')}
                    </Text>
                </div>

                <div className="mx-4 mt-4 flex flex-col gap-2">
                    {/* Jamais répondu : l'amorce n'a pas encore été présentée, ou a été
                        laissée à « Plus tard ». Elle reste accessible depuis cet écran
                        plutôt que d'attendre le prochain démarrage (#80). */}
                    {permission === 'default' && (
                        <Button
                            libelle={t('notifications.activer')}
                            onPress={() => setAmorceOuverte(true)}
                            pleineLargeur
                        />
                    )}

                    <Button
                        libelle={
                            testEnvoye === null
                                ? t('notifications.etat.envoyerTest')
                                : testEnvoye > 0
                                  ? t('notifications.etat.testEnvoye')
                                  : t('notifications.etat.aucunAppareil')
                        }
                        onPress={() => {
                            void envoyerNotificationDeTest()
                                .then(setTestEnvoye)
                                .catch(() => setTestEnvoye(0));
                        }}
                        variante="secondaire"
                        pleineLargeur
                    />

                    {/* Le réenregistrement en un geste, SANS redemander la permission
                        (#21) : l'abonnement perdu en silence est le quatrième état de
                        #20, et il se répare sans jamais risquer un refus définitif. */}
                    {permission === 'granted' && enregistre === false && (
                        <Button
                            libelle={t('notifications.etat.reenregistrer')}
                            onPress={() =>
                                void reabonner().then(() => {
                                    setVerification((v) => v + 1);
                                    void refetch();
                                })
                            }
                            pleineLargeur
                        />
                    )}

                    {/* Sous confirmation : il coupe TOUS les appareils, pas celui-ci. */}
                    <BlocDeconnexionAppareils />
                </div>
            </div>
        </Screen>
    );
}
